import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentLayout } from 'website/components/Layout/components/ContentLayout';

import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Box,
  useMediaQuery,
  Button,
} from '@mui/material';
import { IconWrapper } from 'components/Icon';
import { paths } from 'routes';

import {
  suppliersAllFeatures,
  suppliersTiers,
  buyersAllFeatures,
  buyersTiers,
} from 'website/data/pricing';

export const Pricing = () => {
  const { t } = useTranslation();
  const tableRef = useRef(null);
  const fixedRowsRef = useRef([]);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const [ pricingTab, setPricingTab ] = useState("supplier");
  const [allFeatures, setAllFeatures] = useState(suppliersAllFeatures);
  const [tiers, setTiers] = useState(suppliersTiers);
  const [showFixedRows, setShowFixedRows] = useState(true);

  useEffect(() => {
    if (pricingTab === 'supplier') {
      setAllFeatures(suppliersAllFeatures);
      setTiers(suppliersTiers);
    } else {
      setAllFeatures(buyersAllFeatures);
      setTiers(buyersTiers);
    }
  }, [pricingTab]);

  useEffect(() => {
    const updateWidth = () => {
      if (tableRef.current) {
        const tableWidth = tableRef.current.getBoundingClientRect().width;
        fixedRowsRef.current.forEach(row => {
          if (row) {
            row.style.width = `${tableWidth}px`;
          }
        });
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const addRowRef = (el) => {
    if (el && !fixedRowsRef.current.includes(el)) {
      fixedRowsRef.current.push(el);
    }
  };

  useEffect(() => {
    if (!isMobile) {
      setShowFixedRows(true);
      return;
    }

    // Hide fixed rows when the table has been fully scrolled past.
    // Compute the bottom edge of the visible header rows (their
    // boundingClientRect().bottom) and hide fixed rows once the table
    // bottom is above that value.
    const getHeaderBottom = () => {
      const rows = fixedRowsRef.current.filter(Boolean);
      if (!rows.length) return 70; // fallback to the header's top value
      const bottoms = rows.map(r => {
        try {
          return r.getBoundingClientRect().bottom;
        } catch (e) {
          return 0;
        }
      });
      return Math.max(...bottoms);
    };

    const checkTableBottom = () => {
      const table = tableRef.current;
      if (!table) {
        setShowFixedRows(true);
        return;
      }
      const rect = table.getBoundingClientRect();
      const headerBottom = getHeaderBottom();
      // If the bottom of the table is above the header bottom, hide rows
      const tableBottomAboveHeader = rect.bottom <= headerBottom + 1;
      setShowFixedRows(!tableBottomAboveHeader);
    };

    checkTableBottom();
    window.addEventListener('scroll', checkTableBottom, { passive: true });
    window.addEventListener('resize', checkTableBottom);

    return () => {
      window.removeEventListener('scroll', checkTableBottom);
      window.removeEventListener('resize', checkTableBottom);
    };
  }, [isMobile]);

  return (
    <ContentLayout>
      <Container
        maxWidth={false}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: '0px',
          margin: '0 auto',
          my: 4,
          p: {xs: 0, md: 0},
          px: {xs: 4, md: 0},
        }}
      >
      <>
      <TableContainer sx={{width: '100%', tableLayout: "fixed", overflowX: "initial"}}>
        <Table sx={{ width: '100%', tableLayout: "fixed", mt: {xs: 16.125, md: 22.125} }} aria-label={t('website:pricing.comparisonTableLabel')} ref={tableRef}>
          <TableHead>
          <TableRow
            ref={addRowRef}
            sx={{
              position: 'fixed',
              top: 70,
              height: 30,
              zIndex: 10,
              bgcolor: '#fff',
              display: { xs: 'flex', md: 'none' },
              width: 1,
              maxWidth: 600,
              visibility: showFixedRows ? 'visible' : 'hidden',
            }}
          />
          <TableRow
            ref={addRowRef}
            sx={{
              position: 'fixed',
              top: 100,
              height: 60,
              zIndex: 10,
              bgcolor: 'red',
              display: { xs: 'flex', md: 'none' },
              width: 1,
              visibility: showFixedRows ? 'visible' : 'hidden',
            }}
          >
            <TableCell key={'1'} align="center" sx={{ backgroundColor: '#fff', borderBottom: 'none', width: 1, flex: 1 }} />
            <TableCell onClick={() => setPricingTab("supplier")} key={'supplier'} align="center" sx={{ backgroundColor: pricingTab === 'supplier' ? 'background.primary' : '#fff', borderBottom: 'none', width: 1, flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  lineHeight: "28px",
                  color: pricingTab === 'supplier' ? '#fff' : 'text.custom',
                  flex: 1,
                  textAlign: "center",
                  cursor: "pointer"
                }}
              >
                {t('website:pricing.tabs.suppliers')}
              </Typography>
            </TableCell>
            <TableCell onClick={() => setPricingTab("buyer")} key={'buyer'} align="center" sx={{ backgroundColor: pricingTab === 'buyer' ? 'background.primary' : '#fff', borderBottom: 'none', width: 1, flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  lineHeight: "28px",
                  color: pricingTab === 'buyer' ? '#fff' : 'text.custom',
                  flex: 1,
                  textAlign: "center",
                  cursor: "pointer"
                }}
              >
                {t('website:pricing.tabs.buyers')}
              </Typography>
            </TableCell>
            <TableCell key={'4'} align="center" sx={{ backgroundColor: '#fff', borderBottom: 'none', width: 1, flex: 1 }} />
          </TableRow>
          <TableRow
            ref={addRowRef}
            sx={{
              position: 'fixed',
              top: 160,
              height: 30,
              zIndex: 10,
              bgcolor: '#fff',
              display: { xs: 'flex', md: 'none' },
              width: 1,
              maxWidth: 600,
              visibility: showFixedRows ? 'visible' : 'hidden',
            }}
          />
          <TableRow
            ref={addRowRef}
            sx={{
              position: 'fixed',
              top: 190,
              zIndex: 10,
              bgcolor: '#fff',
              display: { xs: 'flex', md: 'none' },
              visibility: showFixedRows ? 'visible' : 'hidden',
            }}
          >
            {tiers.map((tier) => (
              <TableCell key={tier.title} align="center" sx={{ backgroundColor: tier.focused ? 'background.primary' : '#fff', borderBottom: 'none', pb: 1, width: 1, flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    lineHeight: "28px",
                    color: tier.focused ? '#fff' : 'text.custom',
                    flex: 1,
                    textAlign: "center"
                  }}
                >
                  {t(`website:pricing.tiers.${tier.title}`)}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
          <TableRow
            ref={addRowRef}
            sx={{
              position: 'fixed',
              top: 70,
              height: 44,
              zIndex: 10,
              bgcolor: '#fff',
              display: { xs: 'none', md: 'flex' },
              width: 1,
              visibility: showFixedRows ? 'visible' : 'hidden',
            }}
          />
          <TableRow
            ref={addRowRef}
            sx={{
              position: 'fixed',
              top: 114,
              height: 60,
              zIndex: 10,
              bgcolor: 'red',
              display: { xs: 'none', md: 'flex' },
              width: 1,
              visibility: showFixedRows ? 'visible' : 'hidden',
            }}
          >
            <TableCell key={'1'} align="center" sx={{ backgroundColor: '#fff', borderBottom: 'none', width: 1, flex: 1 }} />
            <TableCell onClick={() => setPricingTab("supplier")} key={'supplier'} align="center" sx={{ backgroundColor: pricingTab === 'supplier' ? 'background.primary' : '#fff', borderBottom: 'none', width: 1, flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  lineHeight: "28px",
                  color: pricingTab === 'supplier' ? '#fff' : 'text.custom',
                  flex: 1,
                  textAlign: "center",
                  cursor: "pointer"
                }}
              >
                {t('website:pricing.tabs.suppliers')}
              </Typography>
            </TableCell>
            <TableCell onClick={() => setPricingTab("buyer")} key={'buyer'} align="center" sx={{ backgroundColor: pricingTab === 'buyer' ? 'background.primary' : '#fff', borderBottom: 'none', width: 1, flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  lineHeight: "28px",
                  color: pricingTab === 'buyer' ? '#fff' : 'text.custom',
                  flex: 1,
                  textAlign: "center",
                  cursor: "pointer"
                }}
              >
                {t('website:pricing.tabs.buyers')}
              </Typography>
            </TableCell>
            <TableCell key={'4'} align="center" sx={{ backgroundColor: '#fff', borderBottom: 'none', width: 1, flex: 1 }} />
          </TableRow>
          <TableRow
            ref={addRowRef}
            sx={{
              position: 'fixed',
              top: 174,
              height: 44,
              zIndex: 10,
              bgcolor: '#fff',
              display: { xs: 'none', md: 'flex' },
              width: 1,
              visibility: showFixedRows ? 'visible' : 'hidden',
            }}
          />
          <TableRow
            ref={addRowRef}
            sx={{
              position: 'fixed',
              top: 218,
              flex: 1,
              zIndex: 10,
              display: { xs: 'none', md: 'flex' },
              bgcolor: "#fff",
              visibility: showFixedRows ? 'visible' : 'hidden',
            }}>
            <TableCell sx={{ borderBottom: 'none', width: 1, flex: 1 }} />
            {tiers.map((tier) => (
              <TableCell key={tier.title} sx={{ backgroundColor: tier.focused ? 'background.primary' : '#fff', borderBottom: 'none', pb: 1, width: 1, flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '2rem',
                    fontWeight: 600,
                    lineHeight: 1.15625,
                    color: tier.focused ? '#fff' : 'text.custom',
                    flex: 1,
                    textAlign: "center"
                  }}
                >
                  {t(`website:pricing.tiers.${tier.title}`)}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
          <TableRow sx={{display: {xs: 'table-row', md: 'table-row'}}}>
            <TableCell sx={{display: {xs: 'none', md: 'table-cell'}}}/>
            {tiers.map((tier) => (
              <TableCell
                key={tier.price}
                align="center"
                sx={{ backgroundColor: tier.focused ? 'background.primary' : '#fff', pt: 1 }}
              >
                <Box>
                  <Box justifyContent={"center"} display={"flex"} flexDirection={{xs: "column", sm: "row"}}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontSize: {xs: '1.125rem', md: '2rem'},
                        fontWeight: 600,
                        lineHeight: 1.15625,
                        color: tier.focused ? "#fff" : "text.custom",
                        textAlign: "center"
                      }}
                    >
                      {tier.price}
                    </Typography>
                    <Typography variant="subtitle1" display={{xs: "none", sm: "block"}} sx={{whiteSpace: 'pre-wrap'}}>
                      {' '}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontSize: {xs: '1.125rem', md: '2rem'},
                        fontWeight: 600,
                        lineHeight: 1.15625,
                        color: tier.focused ? "#fff" : "text.custom",
                        textAlign: "center"
                      }}
                    >
                      {tier.currency}
                    </Typography>
                  </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontSize: {xs: '0.7rem', md: '1.25rem'},
                        fontWeight: 500,
                        lineHeight: 1,
                        color: tier.focused ? "#fff" : "text.custom",
                        textAlign: "center"
                      }}
                    >
                     {t('website:pricing.perMonthPerUser')}
                    </Typography>
                  </Box>
              </TableCell>
            ))}
          </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(allFeatures).map((featureGroup) => (
              <>
                <TableRow sx={{ backgroundColor: 'background.pricing' }}>
                  <TableCell colSpan={isMobile ? tiers.length : tiers.length + 1} sx={{p:1.25}}>
                    <Typography variant="subtitle2" sx={{fontWeight: 600, fontSize: "1.125rem", lineHeight: "28px"}}>{t(`website:pricing.featureGroups.${featureGroup}`)}</Typography>
                  </TableCell>
                </TableRow>
                {allFeatures[featureGroup].map((feature) => (
                  <>
                  <TableRow
                    sx={{
                      display: { xs: 'table-row', md: 'none' },
                    }}
                  >
                    <TableCell colSpan={tiers.length} sx={{borderBottom: 'none'}}>
                    <Box sx={{display: 'flex', alignItems: 'center', fontWeight: 500, lineHeight: "24px"}}>
                        <Tooltip
                          title={t(`website:pricing.suppliers.features.${feature.id}.description`, { defaultValue: t('website:pricing.noDescription') })}
                          placement="top-start"
                          enterTouchDelay={1}
                          leaveTouchDelay={5000}
                          slotProps={{
                            popper: {
                              modifiers: [
                                {
                                  name: 'offset',
                                  options: {
                                    offset: [10, -10]
                                  }
                                }
                              ]
                            },
                            tooltip: {
                              sx: {
                                color: "#fff",
                                backgroundColor: "background.primary",
                                maxWidth: '200px',
                                border: "0.806px solid background.primary",
                                borderRadius: '19.72px',
                                padding: 2,
                                fontSize: '1rem',
                                fontWeight: 400,
                                lineHeight: '1.487',
                                letterSpacing: "-0.08px",
                                textAlign: 'left',
                              },
                            },
                          }}
                        >
                          <Box sx={{mr:0.5}}>
                            <IconWrapper
                              icon="info"
                              sx={({palette}) => ({
                                svg: {
                                  stroke: palette.background.primary,
                                  strokeWidth:2,
                                  height: 16,
                                  width: 16,
                                  mt: -0.3,
                                }
                              })}
                            />
                          </Box>
                        </Tooltip>
                        {t(`website:pricing.suppliers.features.${feature.id}.name`)}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow
                    key={feature}
                    sx={{ 
                      borderBottom: '2px solid #F3F3F3',
                      px: 1,
                      width: 1,
                      height: 40,
                    }}
                  >
                    <TableCell component="th" scope="row" sx={{p:1.25, display: {xs: "none", md: "table-cell"}}}>
                      <Box sx={{display: 'flex', alignItems: 'center', fontWeight: 500, lineHeight: "24px"}}>
                        <Tooltip
                          title={t(`website:pricing.suppliers.features.${feature.id}.description`, { defaultValue: t('website:pricing.noDescription') })}
                          placement="top-start"
                          slotProps={{
                            popper: { 
                              modifiers: [
                                {
                                  name: 'offset', 
                                  options: { 
                                    offset: [10, -10] 
                                  } 
                                }
                              ]
                            },
                            tooltip: {
                              sx: {
                                color: "#fff",
                                backgroundColor: "background.primary",
                                maxWidth: '200px',
                                border: "0.806px solid background.primary",
                                borderRadius: '19.72px',
                                padding: 2,
                                fontSize: '1rem',
                                fontWeight: 400,
                                lineHeight: '1.487',
                                letterSpacing: "-0.08px",
                                textAlign: 'left',
                              },
                            },
                          }}
                        >
                          <Box sx={{mr:0.5}}>
                            <IconWrapper
                              icon="info"
                              sx={({palette}) => ({
                                svg: {
                                  stroke: palette.background.primary,
                                  strokeWidth:2,
                                  height: 16,
                                  width: 16,
                                  mt: -0.3,
                                }
                              })}
                            />
                          </Box>
                        </Tooltip>
                        {t(`website:pricing.suppliers.features.${feature.id}.name`)}
                      </Box>
                    </TableCell>
                    {tiers.map((tier, index) => (
                      <TableCell key={tier.title + index} align="center"  sx={{p:1.25}}>
                        {tier.features[featureGroup]?.includes(feature.id) && (
                          <IconWrapper
                          icon="confirm"
                          sx={({palette}) => ({
                            svg: {
                              stroke: "#2A6672",
                              strokeWidth:2,
                              height: 16,
                              width: 16,
                            }
                          })}
                        />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  </>
                ))}
              </>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </>
    </Container>
    <Button
      size="medium"
      variant="contained"
      component="a"
      href={paths.auth.register}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        backgroundColor: "#62A7B7",
        '&:hover': {
          backgroundColor: "#62A7B7",
        },
        minWidth: 200,
        height: 48,
        whiteSpace: 'nowrap',
        fontSize: "1rem",
        fontWeight: "700",
        color: "#fff",
        lineHeight: 1.5,
        display: {xs: "flex", md: "none"},
        mb: 3,
        mt: -9,
      }}
    >
      {t('website:pricing.signUpFree')}
    </Button>
    </ContentLayout>
  );
};